import { createContext, Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { LmsPrintPreviewPdfButton } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import { useDesignatedReviews } from '@/hooks/coaching/useDesignatedReview'
import { useTests } from '@/hooks/coaching/useTests'
import { useUnitAssessments } from '@/hooks/coaching/useUnitAssessments'
import type { UaSummary } from '@/api/unitAssessmentAdmin'
import { LessonHistoryModal } from './LessonHistoryModal'
import { findActiveCumulativeTest, findPendingOfflineTest } from './coachingDefaultTarget'
import { CollapsibleListHeader } from './CollapsibleListHeader'
import type {
  ComponentStatus,
  CourseNodeView,
  CurriculumNodeView,
  LcTestSummaryView,
  DesignatedReviewView,
  LessonSelectionMeta,
  LibraryLessonNodeView,
  ModuleNodeView,
  TestNodeView,
  TestSelection,
} from '@/types/coaching'

interface Props {
  learnerId: number
  collapsed?: boolean
  onToggleCollapsed?: () => void
  /** 테스트 노드 클릭 → 채점 영역 진입(Phase 5b). 미제공 시 트리 노드는 비클릭 표시만. */
  onSelectTest?: (sel: TestSelection) => void
  /** 원생 상세의 인쇄 진입점 전용 PDF action. 코칭 선택 동작과 별개다. */
  onDownloadTest?: (sel: TestSelection) => void
  /** 선택 강조할 챕터/누적 테스트. */
  selectedTest?: TestSelection | null
  /** 레슨 행 클릭 → 채점/기록 진입(§6-① 정책 A). 미제공 시 레슨 행은 비클릭. */
  onSelectLesson?: (lessonId: number, meta: LessonSelectionMeta) => void
  /** 선택 강조할 레슨. */
  selectedLessonId?: number | null
  /** 단원평가 특강 노드 클릭 → 채점/완료 진입. */
  onSelectUnitAssessment?: (id: number) => void
  /** 선택 강조할 단원평가 특강. */
  selectedUnitAssessmentId?: number | null
  /** 복습 레슨 노드 클릭 → 예약 관리 또는 진행·완료 복습 코칭 진입. */
  onSelectDesignatedReview?: (lessonId: number) => void
  /** 선택 강조할 복습 예약 레슨. */
  selectedDesignatedReviewLessonId?: number | null
}

type ViewHistory = (lesson: LibraryLessonNodeView) => void

const testNodeKey = (kind: string, chapterOrdinal: number, scopeContentId: number) =>
  `${kind}-${chapterOrdinal}-${scopeContentId}`

const EMPTY_TREE: CurriculumNodeView[] = []
const EMPTY_REVIEWS: DesignatedReviewView[] = []

function progressStatusKey(done: number, total: number): 'notStarted' | 'inProgress' | 'completed' {
  if (total > 0 && done >= total) return 'completed'
  if (done > 0) return 'inProgress'
  return 'notStarted'
}

function lessonStatusKey(status: ComponentStatus): 'notStarted' | 'learning' | 'completed' {
  if (status === 'completed') return 'completed'
  if (status === 'in_progress') return 'learning'
  return 'notStarted'
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** 트리 테스트 노드의 실 상태/점수 머지 + 클릭 핸들러를 깊은 노드까지 전달(프롭 드릴링 회피). */
const TreeTestContext = createContext<{
  statusByKey: Map<string, LcTestSummaryView>
  onSelectTest?: (sel: TestSelection) => void
  onDownloadTest?: (sel: TestSelection) => void
  selectedTest?: TestSelection | null
  currentCumulativeAssessmentId?: number | null
}>({ statusByKey: new Map() })

/** 레슨 행 클릭 선택/오늘 뱃지/강조를 깊은 노드까지 전달. */
const TreeLessonContext = createContext<{
  onSelectLesson?: (lessonId: number, meta: LessonSelectionMeta) => void
  selectedLessonId?: number | null
  currentLessonId?: number | null
}>({ currentLessonId: null })

/** 단원평가 특강 노드(anchor 레슨 '전' 타임라인)를 깊은 노드까지 전달. */
const TreeUaContext = createContext<{
  uaByAnchor: Map<number, UaSummary[]>
  selectedUnitAssessmentId?: number | null
  onSelectUnitAssessment?: (id: number) => void
}>({ uaByAnchor: new Map() })

/** 지정 복습 노드(anchor 레슨 '전' 타임라인)를 깊은 노드까지 전달. */
const TreeReviewContext = createContext<{
  reviewsByAnchor: Map<number, DesignatedReviewView[]>
  selectedDesignatedReviewLessonId?: number | null
  onSelectDesignatedReview?: (lessonId: number) => void
}>({ reviewsByAnchor: new Map() })

function uaStatusKey(status: string): string {
  switch (status) {
    case 'COMPLETED':
      return 'completed'
    case 'REVIEW_OPEN':
      return 'reviewOpen'
    case 'GRADED':
      return 'graded'
    case 'SUBMITTED':
      return 'submitted'
    case 'STARTED':
      return 'started'
    case 'CANCELLED':
      return 'cancelled'
    default:
      return 'pending'
  }
}

type SupplementalLearning =
  | { kind: 'review'; review: DesignatedReviewView; sourceIndex: number }
  | { kind: 'unitAssessment'; unitAssessment: UaSummary; sourceIndex: number }

function supplementalLearningCompleted(item: SupplementalLearning): boolean {
  return item.kind === 'review'
    ? item.review.placementStatus === 'COMPLETED'
    : item.unitAssessment.status === 'COMPLETED'
}

function supplementalLearningTime(item: SupplementalLearning): string {
  if (item.kind === 'review') {
    return item.review.completedAt ?? item.review.createdAt
  }
  return item.unitAssessment.completedAt ?? item.unitAssessment.createdAt
}

function pendingLearningOrder(item: SupplementalLearning): number {
  if (item.kind === 'unitAssessment') {
    return item.unitAssessment.status === 'PENDING' ? 4 : 5
  }

  switch (item.review.placementStatus) {
    case 'SCHEDULED':
      return 0
    case 'ANCHOR_IN_PROGRESS':
      return 1
    case 'STARTED':
      return 3
    default:
      return 2
  }
}

/**
 * 같은 anchor 앞의 보조 학습을 실제 진행 타임라인으로 정렬한다.
 * 완료 이력은 완료 시각순, 앞으로 수행할 항목은 예약 → due/started → hard-lock 단원평가 순이다.
 * 따라서 현재/최신 학습이 anchor 레슨에 가장 가까운(화면상 가장 아래) 위치에 온다.
 */
function compareSupplementalLearning(a: SupplementalLearning, b: SupplementalLearning): number {
  const aCompleted = supplementalLearningCompleted(a)
  const bCompleted = supplementalLearningCompleted(b)
  if (aCompleted !== bCompleted) return aCompleted ? -1 : 1

  if (aCompleted) {
    const byTime = supplementalLearningTime(a).localeCompare(supplementalLearningTime(b))
    if (byTime !== 0) return byTime
  } else {
    const byExecutionOrder = pendingLearningOrder(a) - pendingLearningOrder(b)
    if (byExecutionOrder !== 0) return byExecutionOrder
  }

  if (a.kind !== b.kind) return a.kind === 'review' ? -1 : 1
  return a.sourceIndex - b.sourceIndex
}

function findCurrentLessonId(
  tree: CurriculumNodeView[],
  pendingChapterTestLessonId: number | null,
): number | null {
  let firstInProgress: number | null = null
  let firstNotStarted: number | null = null

  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        for (const lesson of module.lessons) {
          if (firstInProgress == null && lesson.status === 'in_progress') {
            firstInProgress = lesson.lessonId
          }
          if (firstNotStarted == null && lesson.status === 'not_started') firstNotStarted = lesson.lessonId
        }
      }
    }
  }

  return pendingChapterTestLessonId ?? firstInProgress ?? firstNotStarted
}

function findDefaultExpansionLessonId(
  reviews: DesignatedReviewView[],
  currentLessonId: number | null,
): number | null {
  const activeReviews = reviews.filter((review) => review.placementStatus !== 'COMPLETED')
  return activeReviews.find((review) => review.anchorLessonId != null)?.anchorLessonId
    ?? activeReviews[0]?.reviewLessonId
    ?? currentLessonId
}

function findDefaultExpansion(tree: CurriculumNodeView[], currentLessonId: number | null) {
  const courseIds = new Set<number>()
  const chapterIds = new Set<number>()

  if (currentLessonId == null) return { courseIds, chapterIds, key: 'none' }

  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        if (module.lessons.some((lesson) => lesson.lessonId === currentLessonId)) {
          courseIds.add(course.courseId)
          chapterIds.add(module.moduleId)
          return {
            courseIds,
            chapterIds,
            key: `${course.courseId}:${module.moduleId}:${currentLessonId}`,
          }
        }
      }
    }
  }

  return { courseIds, chapterIds, key: 'none' }
}

/**
 * 콘텐츠 트리(과정>코스>모듈(=챕터)>레슨) 본체 — plan §6 안 A "콘텐츠 트리" 탭.
 * Phase 2 BE 집계를 소비만 한다. 테스트 노드는 Phase 5 의존이라 표시하지 않는다.
 * 과정 메트릭(완료율 게이지·누적/사용/미사용 칩)은 LearnerMetricsBar(중 pane 상단)로 승격되어
 * 여기서는 중복 제거했다. 단 트리 탭 단독 맥락을 위해 과정 뱃지 + 코스/챕터 집계는 유지한다.
 */
export function ContentTreeSection({
  learnerId,
  collapsed = false,
  onToggleCollapsed,
  onSelectTest,
  onDownloadTest,
  selectedTest,
  onSelectLesson,
  selectedLessonId,
  onSelectUnitAssessment,
  selectedUnitAssessmentId,
  onSelectDesignatedReview,
  selectedDesignatedReviewLessonId,
}: Props) {
  const { t } = useTranslation('coaching')
  const library = useLibrary(learnerId)
  const tests = useTests(learnerId)
  const unitAssessments = useUnitAssessments(learnerId)
  const designatedReviews = useDesignatedReviews(learnerId)
  const [historyLesson, setHistoryLesson] = useState<LibraryLessonNodeView | null>(null)
  const tree = library.data?.tree ?? EMPTY_TREE
  const reviews = designatedReviews.data ?? EMPTY_REVIEWS
  const pendingOfflineTest = useMemo(
    () => findPendingOfflineTest(tree, tests.data?.tests),
    [tests.data?.tests, tree],
  )
  const activeCumulativeTest = useMemo(
    () => (pendingOfflineTest == null ? findActiveCumulativeTest(tree) : null),
    [pendingOfflineTest, tree],
  )
  // 미완료 누적 테스트가 있으면 현재 항목은 레슨이 아니라 그 테스트다.
  const currentLessonId = activeCumulativeTest
    ? null
    : findCurrentLessonId(tree, pendingOfflineTest?.prerequisiteLessonId ?? null)
  const currentCumulativeAssessmentId = activeCumulativeTest?.selection.cumulativeAssessmentId ?? null
  const defaultExpansionLessonId = findDefaultExpansionLessonId(
    reviews,
    currentLessonId ?? activeCumulativeTest?.predecessorLessonId ?? null,
  )

  const reviewsByAnchor = useMemo(() => {
    const result = new Map<number, DesignatedReviewView[]>()
    for (const review of reviews) {
      const anchorLessonId = review.anchorLessonId ?? review.reviewLessonId
      const anchoredReviews = result.get(anchorLessonId) ?? []
      anchoredReviews.push(review)
      result.set(anchorLessonId, anchoredReviews)
    }
    return result
  }, [reviews])

  const uaByAnchor = useMemo(() => {
    const m = new Map<number, UaSummary[]>()
    const ordered = [...(unitAssessments.data ?? [])].sort(
      (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id,
    )
    for (const ua of ordered) {
      if (ua.status === 'CANCELLED') continue
      const arr = m.get(ua.anchorLessonId) ?? []
      arr.push(ua)
      m.set(ua.anchorLessonId, arr)
    }
    return m
  }, [unitAssessments.data])
  const defaultExpansion = useMemo(
    () => findDefaultExpansion(tree, defaultExpansionLessonId),
    [tree, defaultExpansionLessonId],
  )
  const [expandedCourseIds, setExpandedCourseIds] = useState<Set<number>>(new Set())
  const [expandedChapterIds, setExpandedChapterIds] = useState<Set<number>>(new Set())

  const statusByKey = useMemo(() => {
    const m = new Map<string, LcTestSummaryView>()
    for (const tst of tests.data?.tests ?? []) {
      m.set(testNodeKey(tst.kind, tst.chapterOrdinal, tst.scopeContentId), tst)
    }
    return m
  }, [tests.data])

  useEffect(() => {
    setExpandedCourseIds(new Set(defaultExpansion.courseIds))
    setExpandedChapterIds(new Set(defaultExpansion.chapterIds))
  }, [defaultExpansion.key, defaultExpansion.courseIds, defaultExpansion.chapterIds])

  if (library.isLoading) {
    return (
      <section className="overflow-hidden rounded-lg border border-gray-200" role="status" aria-live="polite">
        <CollapsibleListHeader
          title={t('library.title')}
          collapsed={collapsed}
          controlsId="coaching-content-tree-body"
          collapseLabel={t('panelCollapse.lessonList.collapse')}
          expandLabel={t('panelCollapse.lessonList.expand')}
          onToggle={onToggleCollapsed}
        />
        <p hidden={collapsed} className="p-4 text-sm text-gray-500">{t('library.title')}…</p>
      </section>
    )
  }
  if (library.error) {
    return (
      <section className="overflow-hidden rounded-lg border border-gray-200" role="alert">
        <CollapsibleListHeader
          title={t('library.title')}
          collapsed={collapsed}
          controlsId="coaching-content-tree-body"
          collapseLabel={t('panelCollapse.lessonList.collapse')}
          expandLabel={t('panelCollapse.lessonList.expand')}
          onToggle={onToggleCollapsed}
        />
        <p hidden={collapsed} className="p-4 text-sm text-red-600">{t('library.loadError')}</p>
      </section>
    )
  }

  const onViewHistory: ViewHistory = setHistoryLesson

  const toggleCourse = (courseId: number) => {
    setExpandedCourseIds((prev) => {
      const next = new Set(prev)
      if (next.has(courseId)) next.delete(courseId)
      else next.add(courseId)
      return next
    })
  }

  const toggleChapter = (chapterId: number) => {
    setExpandedChapterIds((prev) => {
      const next = new Set(prev)
      if (next.has(chapterId)) next.delete(chapterId)
      else next.add(chapterId)
      return next
    })
  }

  return (
    <section className="overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm" data-testid="content-tree">
      <CollapsibleListHeader
        title={t('library.title')}
        collapsed={collapsed}
        controlsId="coaching-content-tree-body"
        collapseLabel={t('panelCollapse.lessonList.collapse')}
        expandLabel={t('panelCollapse.lessonList.expand')}
        onToggle={onToggleCollapsed}
      />
      <div id="coaching-content-tree-body" data-testid="content-tree-body" hidden={collapsed}>
        {tree.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">{t('library.empty')}</p>
        ) : (
          <TreeTestContext.Provider value={{ statusByKey, onSelectTest, onDownloadTest, selectedTest, currentCumulativeAssessmentId }}>
          <TreeUaContext.Provider
            value={{
              uaByAnchor,
              selectedUnitAssessmentId,
              onSelectUnitAssessment,
            }}
          >
            <TreeReviewContext.Provider value={{
              reviewsByAnchor,
              selectedDesignatedReviewLessonId,
              onSelectDesignatedReview,
            }}>
              <TreeLessonContext.Provider value={{ onSelectLesson, selectedLessonId, currentLessonId }}>
                <div className="divide-y divide-gray-100">
                  {tree.map((c) => (
                    <CurriculumNode
                      key={c.curriculumId}
                      node={c}
                      expandedCourseIds={expandedCourseIds}
                      expandedChapterIds={expandedChapterIds}
                      onToggleCourse={toggleCourse}
                      onToggleChapter={toggleChapter}
                      onViewHistory={onViewHistory}
                    />
                  ))}
                </div>
              </TreeLessonContext.Provider>
            </TreeReviewContext.Provider>
          </TreeUaContext.Provider>
          </TreeTestContext.Provider>
        )}
      </div>
      {historyLesson && (
        <LessonHistoryModal
          learnerId={learnerId}
          lessonId={historyLesson.lessonId}
          lessonTitle={historyLesson.title}
          courseId={historyLesson.courseId}
          moduleId={historyLesson.moduleId}
          onClose={() => setHistoryLesson(null)}
        />
      )}
    </section>
  )
}

function CurriculumNode({
  node,
  expandedCourseIds,
  expandedChapterIds,
  onToggleCourse,
  onToggleChapter,
  onViewHistory,
}: {
  node: CurriculumNodeView
  expandedCourseIds: Set<number>
  expandedChapterIds: Set<number>
  onToggleCourse: (courseId: number) => void
  onToggleChapter: (chapterId: number) => void
  onViewHistory: ViewHistory
}) {
  return (
    <div className="p-4">
      <div className="space-y-3">
        {node.courses.map((course) => (
          <CourseNode
            key={course.courseId}
            node={course}
            expanded={expandedCourseIds.has(course.courseId)}
            expandedChapterIds={expandedChapterIds}
            onToggleCourse={onToggleCourse}
            onToggleChapter={onToggleChapter}
            onViewHistory={onViewHistory}
          />
        ))}
      </div>
    </div>
  )
}

function CourseNode({
  node,
  expanded,
  expandedChapterIds,
  onToggleCourse,
  onToggleChapter,
  onViewHistory,
}: {
  node: CourseNodeView
  expanded: boolean
  expandedChapterIds: Set<number>
  onToggleCourse: (courseId: number) => void
  onToggleChapter: (chapterId: number) => void
  onViewHistory: ViewHistory
}) {
  return (
    <div className={clsx('rounded-md border shadow-sm', expanded ? 'border-gray-300' : 'border-gray-200')}>
      <button
        type="button"
        data-testid={`course-toggle-${node.courseId}`}
        aria-expanded={expanded}
        onClick={() => onToggleCourse(node.courseId)}
        className={clsx(
          'flex w-full items-center justify-between gap-2 rounded-t-md px-3 py-2 text-left transition-colors',
          expanded ? 'bg-gray-100 text-gray-950' : 'rounded-b-md bg-gray-50 text-gray-800 hover:bg-gray-100',
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {expanded ? (
            <ChevronDown className="size-4 shrink-0 text-gray-700" aria-hidden />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-gray-500" aria-hidden />
          )}
          <span className="truncate text-sm font-semibold"><ContentMetadataText text={node.title} /></span>
        </span>
      </button>
      <div
        data-testid={`course-panel-${node.courseId}`}
        className={clsx('space-y-2 px-3 py-2', !expanded && 'hidden')}
      >
        {node.modules.map((m) => (
          <ChapterNode
            key={m.moduleId}
            node={m}
            expanded={expandedChapterIds.has(m.moduleId)}
            onToggleChapter={onToggleChapter}
            onViewHistory={onViewHistory}
          />
        ))}
      </div>
    </div>
  )
}

function ChapterNode({
  node,
  expanded,
  onToggleChapter,
  onViewHistory,
}: {
  node: ModuleNodeView
  expanded: boolean
  onToggleChapter: (chapterId: number) => void
  onViewHistory: ViewHistory
}) {
  const { t } = useTranslation('coaching')
  const tests = node.tests ?? []
  const status = t(`library.status.${progressStatusKey(node.completedLessonCount, node.lessonCount)}`)
  const testUnlocked = (tn: TestNodeView) => {
    const prerequisite = node.lessons[Math.min(tn.afterLessonOrdinal, node.lessons.length) - 1]
    return prerequisite?.status === 'completed'
  }
  return (
    <div
      className={clsx(
        'rounded-md border',
        expanded ? 'border-lc-mint-200 bg-lc-mint-50/30' : 'border-gray-200 bg-white',
      )}
    >
      <button
        type="button"
        data-testid={`chapter-toggle-${node.moduleId}`}
        aria-expanded={expanded}
        onClick={() => onToggleChapter(node.moduleId)}
        className="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left hover:bg-lc-mint-50"
      >
        <span className="flex min-w-0 items-center gap-2">
          {expanded ? (
            <ChevronDown className="size-4 shrink-0 text-lc-mint-800" aria-hidden />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-gray-500" aria-hidden />
          )}
          <span className={clsx('truncate text-sm font-semibold', expanded ? 'text-gray-950' : 'text-gray-700')}>
            <ContentMetadataText text={t('library.chapterTitle', { number: node.chapterNumber, title: node.title })} />
          </span>
        </span>
        <span className={clsx('shrink-0 text-xs font-medium', expanded ? 'text-lc-mint-900' : 'text-gray-500')}>
          {t('library.chapterProgress', { done: node.completedLessonCount, total: node.lessonCount, status })}
        </span>
      </button>
      <ul
        data-testid={`chapter-panel-${node.moduleId}`}
        className={clsx('ml-4 space-y-1 border-l border-lc-mint-200 py-1 pl-3 pr-2', !expanded && 'hidden')}
      >
        {node.lessons.map((l, idx) => (
          <Fragment key={`${l.curriculumId}:${l.courseId}:${l.moduleId}:${l.lessonId}`}>
            {/* 같은 anchor의 복습/단원평가를 학습 순서대로 합쳐 최신 항목을 아래에 둔다. */}
            <SupplementalLearningAnchorRows lessonId={l.lessonId} />
            <LessonRow
              node={l}
              chapterNumber={node.chapterNumber}
              chapterTitle={node.title}
              lessonNumber={idx + 1}
              onViewHistory={onViewHistory}
            />
            {/* Phase 5a — afterLessonOrdinal 위치(레슨 1-based 순번) 뒤에 테스트 노드 인터리브 */}
            {tests
              .filter((tn) => tn.afterLessonOrdinal === idx + 1)
              .map((tn) => (
                <TestRow key={testKey(tn)} node={tn} chapterTitle={node.title} unlocked={testUnlocked(tn)} />
              ))}
          </Fragment>
        ))}
        {/* afterLessonOrdinal 이 레슨 수보다 큰 경우(range 필터 등)는 챕터 끝에 표시 */}
        {tests
          .filter((tn) => tn.afterLessonOrdinal > node.lessons.length)
          .map((tn) => (
            <TestRow key={testKey(tn)} node={tn} chapterTitle={node.title} unlocked={testUnlocked(tn)} />
          ))}
      </ul>
    </div>
  )
}

/** anchor 레슨 앞에 붙는 복습/단원평가 타임라인. */
function SupplementalLearningAnchorRows({ lessonId }: { lessonId: number }) {
  const { reviewsByAnchor } = useContext(TreeReviewContext)
  const { uaByAnchor } = useContext(TreeUaContext)
  const activities: SupplementalLearning[] = [
    ...(reviewsByAnchor.get(lessonId) ?? []).map((review, sourceIndex) => ({
      kind: 'review' as const,
      review,
      sourceIndex,
    })),
    ...(uaByAnchor.get(lessonId) ?? []).map((unitAssessment, sourceIndex) => ({
      kind: 'unitAssessment' as const,
      unitAssessment,
      sourceIndex,
    })),
  ].sort(compareSupplementalLearning)

  if (activities.length === 0) return null

  return (
    <>
      {activities.map((activity) => (
        activity.kind === 'review'
          ? <DesignatedReviewRow key={`designated-review-${activity.review.reviewLessonId}`} review={activity.review} />
          : <UnitAssessmentRow key={`ua-${activity.unitAssessment.id}`} unitAssessment={activity.unitAssessment} />
      ))}
    </>
  )
}

function DesignatedReviewRow({ review }: { review: DesignatedReviewView }) {
  const { t } = useTranslation('coaching')
  const {
    selectedDesignatedReviewLessonId,
    onSelectDesignatedReview,
  } = useContext(TreeReviewContext)
  const selected = selectedDesignatedReviewLessonId === review.reviewLessonId
  const completed = review.placementStatus === 'COMPLETED'

  return (
    <li data-testid={`review-node-${review.reviewLessonId}`}>
      <button
        type="button"
        data-testid={`review-node-btn-${review.reviewLessonId}`}
        disabled={!onSelectDesignatedReview}
        aria-pressed={selected}
        onClick={() => onSelectDesignatedReview?.(review.reviewLessonId)}
        className={clsx(
          'flex w-full flex-wrap items-center gap-2 rounded px-1 py-0.5 text-left text-sm',
          onSelectDesignatedReview
            ? completed ? 'hover:bg-emerald-50/60' : 'hover:bg-amber-50/60'
            : 'cursor-default',
          selected && (completed
            ? 'bg-emerald-100/70 ring-1 ring-emerald-300'
            : 'bg-amber-100/70 ring-1 ring-amber-300'),
        )}
      >
        <span
          className={clsx('inline-block h-2 w-2 shrink-0 rounded-full', completed ? 'bg-emerald-500' : 'bg-amber-400')}
          aria-hidden="true"
        />
        <span className={clsx(
          'inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium',
          completed
            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
            : 'border-amber-100 bg-amber-50 text-amber-700',
        )}>
          {t('library.lessonReviewTag')}
        </span>
        <span className="min-w-0 truncate text-xs text-gray-700"><ContentMetadataText text={review.lessonTitle} /></span>
        <span className="text-xs text-gray-400">
          {t(`designatedReview.cards.status.${review.placementStatus}`)}
        </span>
      </button>
    </li>
  )
}

function testKey(tn: TestNodeView): string {
  return `test-${tn.kind}-${tn.chapterOrdinal}`
}

function TestRow({ node, chapterTitle, unlocked }: { node: TestNodeView; chapterTitle: string; unlocked: boolean }) {
  const { t } = useTranslation('coaching')
  const { statusByKey, onSelectTest, onDownloadTest, selectedTest, currentCumulativeAssessmentId } = useContext(TreeTestContext)
  const cumulative = node.kind === 'CUMULATIVE_ASSESSMENT'
  const label = cumulative ? t('library.test.cumulative') : t('library.test.chapter')
  const locallyClickable = import.meta.env.MODE === 'development'
  const clickable = cumulative ? node.cumulativeAssessmentId != null : unlocked || locallyClickable
  const selected = selectedTest?.kind === node.kind
    && selectedTest.chapterOrdinal === node.chapterOrdinal
    && selectedTest.scopeContentId === node.scopeContentId

  // Phase 5b — GET tests 머지: 출제·채점된 테스트의 실 상태/점수. 없으면 미실시(placeholder).
  const summary = cumulative ? undefined : statusByKey.get(testNodeKey(node.kind, node.chapterOrdinal, node.scopeContentId))
  const isCurrent = cumulative && node.cumulativeAssessmentId != null &&
    node.cumulativeAssessmentId === currentCumulativeAssessmentId
  // 누적 테스트는 마감([누적 테스트 완료])까지가 한 건의 절차다. 두 시험이 끝나도 마감 전이면 "진행 중"이라
  // 교사에게 마감이 남아 있다는 신호가 유지된다. 다만 시험을 시작한 뒤로는 "미실시"가 아니다.
  const cumulativeStarted = cumulative &&
    node.cumulativeSegmentProgress != null && node.cumulativeSegmentProgress !== 'NOT_STARTED'
  const statusLabel =
    summary?.status === 'COMPLETED' || (cumulative && node.cumulativeAssessmentStatus === 'RESOLVED')
      ? t('library.test.completed')
      : cumulativeStarted
        ? t('library.test.cumulativeInProgress')
        : summary?.status === 'IN_PROGRESS' || summary?.status === 'GENERATED'
          ? t('library.test.inProgress')
          : t('library.test.notStarted')

  const meta = (
    <>
      <span className="inline-flex items-center rounded border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
        {label}
      </span>
      <span className="text-xs text-gray-400">{statusLabel}</span>
      {summary?.status === 'COMPLETED' && summary.score != null && (
        <span className="text-xs text-gray-500">{t('library.lessonCorrectRate', { rate: summary.score })}</span>
      )}
      {summary?.status === 'COMPLETED' && summary.completedAt && (
        <span className="text-xs text-gray-500">{formatDateTime(summary.completedAt)}</span>
      )}
      {!unlocked && <span className="text-xs text-gray-400">{t('library.lockedUntilPreviousLesson')}</span>}
    </>
  )

  return (
    <li data-testid={`test-node-${node.kind.toLowerCase()}-${node.chapterOrdinal}`}>
      {onSelectTest ? (
        <>
          <button
            type="button"
            data-testid={`test-node-btn-${node.kind.toLowerCase()}-${node.chapterOrdinal}`}
            disabled={!clickable}
            aria-pressed={selected}
            onClick={() =>
              clickable &&
              onSelectTest({
                kind: node.kind,
                chapterOrdinal: node.chapterOrdinal,
                scopeContentId: node.scopeContentId,
                cumulativeAssessmentId: node.cumulativeAssessmentId,
                chapterTitle,
                ...(!unlocked && locallyClickable ? { forcePrerequisite: true } : {}),
              })
            }
            className={clsx(
              'flex w-full flex-wrap items-center gap-2 rounded px-1 py-0.5 text-left text-sm',
              clickable ? 'hover:bg-indigo-50/50' : 'cursor-not-allowed opacity-55',
              selected && 'bg-indigo-100/70 ring-1 ring-indigo-300',
            )}
          >
            {/* dot 은 순수 시각 장식 — 라벨은 옆 badge 텍스트가 제공하므로 aria-hidden(중복 announce 방지) */}
            <span
              data-testid={`test-node-dot-${node.kind.toLowerCase()}-${node.chapterOrdinal}`}
              className={clsx('inline-block h-2 w-2 shrink-0 rounded-full', isCurrent ? 'bg-amber-400' : 'bg-indigo-300')}
              aria-hidden="true"
            />
            {meta}
          </button>
          {onDownloadTest && !cumulative && (
            <LmsPrintPreviewPdfButton
              disabled={!clickable}
              onClick={() => onDownloadTest({
                kind: node.kind,
                chapterOrdinal: node.chapterOrdinal,
                scopeContentId: node.scopeContentId,
                cumulativeAssessmentId: node.cumulativeAssessmentId,
                chapterTitle,
              })}
            />
          )}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span
            data-testid={`test-node-dot-${node.kind.toLowerCase()}-${node.chapterOrdinal}`}
            className={clsx('inline-block h-2 w-2 shrink-0 rounded-full', isCurrent ? 'bg-amber-400' : 'bg-indigo-300')}
            aria-hidden="true"
          />
          {meta}
        </div>
      )}
    </li>
  )
}

function UnitAssessmentRow({ unitAssessment: ua }: { unitAssessment: UaSummary }) {
  const { t } = useTranslation('coaching')
  const { selectedUnitAssessmentId, onSelectUnitAssessment } = useContext(TreeUaContext)
  const selected = selectedUnitAssessmentId === ua.id

  return (
    <li data-testid={`ua-node-${ua.id}`}>
      <button
        type="button"
        data-testid={`ua-node-btn-${ua.id}`}
        disabled={!onSelectUnitAssessment}
        aria-pressed={selected}
        onClick={() => onSelectUnitAssessment?.(ua.id)}
        className={clsx(
          'flex w-full flex-wrap items-center gap-2 rounded px-1 py-0.5 text-left text-sm',
          onSelectUnitAssessment ? 'hover:bg-rose-50/60' : 'cursor-default',
          selected && 'bg-rose-100/70 ring-1 ring-rose-300',
        )}
      >
        <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-rose-400" aria-hidden="true" />
        <span className="inline-flex items-center rounded border border-rose-100 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
          {t('unitAssessment.treeBadge')}
        </span>
        {ua.setName && <span className="truncate text-xs text-gray-700">{ua.setName}</span>}
        <span className="text-xs text-gray-400">{t(`unitAssessment.mode.${ua.mode}`)}</span>
        <span className="text-xs text-gray-400">{t(`unitAssessment.statusLabel.${uaStatusKey(ua.status)}`)}</span>
        {ua.status === 'COMPLETED' && ua.score != null && (
          <span className="text-xs text-gray-500">{t('unitAssessment.score', { score: ua.score })}</span>
        )}
      </button>
    </li>
  )
}

function LessonRow({
  node,
  chapterNumber,
  chapterTitle,
  lessonNumber,
  onViewHistory,
}: {
  node: LibraryLessonNodeView
  chapterNumber: number
  chapterTitle: string
  lessonNumber: number
  onViewHistory: ViewHistory
}) {
  const { t } = useTranslation('coaching')
  const { onSelectLesson, selectedLessonId, currentLessonId } = useContext(TreeLessonContext)
  const completed = node.status === 'completed'
  const inProgress = node.status === 'in_progress'
  const isCurrent = currentLessonId === node.lessonId
  const clickable = onSelectLesson != null && (isCurrent || completed || inProgress)
  const selected = selectedLessonId === node.lessonId
  const meta: LessonSelectionMeta = {
    curriculumId: node.curriculumId,
    courseId: node.courseId,
    moduleId: node.moduleId,
    chapterNumber,
    chapterTitle,
    lessonNumber,
    lessonTitle: node.title,
    gameKeys: node.gameKeys ?? [],
    status: node.status,
  }

  const inner = (
    <>
      <span className="text-gray-800"><ContentMetadataText text={t('library.lessonTitle', { number: lessonNumber, title: node.title })} /></span>
    </>
  )

  const metaItems = (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-xs text-gray-500">{t(`library.status.${lessonStatusKey(node.status)}`)}</span>
      {(node.gameKeys?.length ?? 0) > 0 && (
        <span
          data-testid="game-math-tag"
          title={node.gameKeys?.join(', ')}
          className="inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700"
        >
          {t('library.gameMathTag')}
        </span>
      )}
      {node.latestCorrectRate != null && (
        <span className="text-xs text-gray-500">{t('library.lessonCorrectRate', { rate: node.latestCorrectRate })}</span>
      )}
    </div>
  )

  return (
    <li data-testid="lesson-row" className="rounded-md">
      <div
        data-testid={`lesson-row-frame-${node.lessonId}`}
        className={clsx(
          'rounded-md px-1.5 py-1',
          clickable && 'hover:bg-lc-mint-50/50',
          selected && 'bg-lc-mint-100/70 ring-1 ring-lc-mint-200',
        )}
      >
        <div className="flex items-start gap-2">
          <StatusDot lessonId={node.lessonId} status={node.status} highlighted={isCurrent} />
          <div className="min-w-0 flex-1">
            {clickable ? (
              <button
                type="button"
                data-testid={`lesson-btn-${node.lessonId}`}
                aria-pressed={selected}
                onClick={() => onSelectLesson!(node.lessonId, meta)}
                className="block w-full text-left text-sm"
              >
                {inner}
                {metaItems}
              </button>
            ) : (
              <div className="text-sm">
                {inner}
                {metaItems}
              </div>
            )}
            {completed && (
              <button
                type="button"
                data-testid={`history-btn-${node.lessonId}`}
                onClick={() => onViewHistory(node)}
                className="mt-0.5 text-xs font-medium text-primary-600 underline-offset-2 hover:underline"
              >
                {t('library.viewHistory')}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

function StatusDot({ lessonId, status, highlighted }: { lessonId: number; status: ComponentStatus; highlighted: boolean }) {
  const { t } = useTranslation('coaching')
  const color = (() => {
    if (highlighted || status === 'in_progress') return 'bg-amber-400'
    if (status === 'completed') return 'bg-lc-mint-500'
    return 'bg-gray-300'
  })()

  return (
    <span
      data-testid={`lesson-status-dot-${lessonId}`}
      className={clsx('mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full', color)}
      role="img"
      aria-label={t(`component.status.${status}`)}
    />
  )
}
